import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Canvas, useLoader } from '@react-three/fiber';
import { Bounds, OrbitControls, Stage } from '@react-three/drei';
import { Download, ExternalLink, FileText, Film, Image, Maximize2, X } from 'lucide-react';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js';
import { Mesh, MeshStandardMaterial, SRGBColorSpace } from 'three';
import { CUSTOMER_API_BASE_URL } from '../config/env';
import { useI18n } from '../i18n';
import { isPinsPath } from '../types/scenePins';

export type AgentArtifactKind = 'video' | 'pdf' | 'obj' | 'image' | 'pins' | 'unknown';

export interface AgentArtifact {
  label: string;
  path: string;
  url: string | null;
  kind: AgentArtifactKind;
  canPreview: boolean;
  mtlUrl?: string;
  textureUrls?: Record<string, string>;
}

const SUPPORTED_EXTENSIONS: Record<string, AgentArtifactKind> = {
  mp4: 'video',
  m4v: 'video',
  mov: 'video',
  webm: 'video',
  pdf: 'pdf',
  obj: 'obj',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  webp: 'image',
};

function basename(path: string): string {
  const cleanPath = path.split('?')[0].split('#')[0];
  return cleanPath.split('/').filter(Boolean).pop() || cleanPath;
}

function extensionOf(path: string): string {
  const cleanPath = path.split('?')[0].split('#')[0];
  return cleanPath.includes('.') ? cleanPath.split('.').pop()!.toLowerCase() : '';
}

function isBrowserReadableUrl(path: string): boolean {
  return /^(https?:\/\/|blob:)/i.test(path) || /^\/(api|assets|si-artifacts)\//i.test(path);
}

function resolveBrowserUrl(path: string): string | null {
  if (/^(https?:\/\/|blob:)/i.test(path)) return path;
  if (path.startsWith('/api/')) return `${CUSTOMER_API_BASE_URL}${path}`;
  if (/^\/(assets|si-artifacts)\//i.test(path)) return path;
  return null;
}

export function getAgentArtifact(
  path: string,
  opts?: { mtlUrl?: string; textureUrls?: Record<string, string> },
): AgentArtifact {
  const kind = isPinsPath(path) ? 'pins' : SUPPORTED_EXTENSIONS[extensionOf(path)] ?? 'unknown';
  const url = isBrowserReadableUrl(path) ? resolveBrowserUrl(path) : null;

  return {
    label: basename(path),
    path,
    url,
    kind,
    canPreview: kind !== 'unknown' && !!url,
    mtlUrl: opts?.mtlUrl,
    textureUrls: opts?.textureUrls,
  };
}

function artifactIcon(kind: AgentArtifactKind) {
  if (kind === 'video') return <Film className="h-4 w-4" />;
  if (kind === 'pdf') return <FileText className="h-4 w-4" />;
  if (kind === 'obj') return <Maximize2 className="h-4 w-4" />;
  if (kind === 'image') return <Image className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
}

// OBJ with MTL + texture — used when a .mtl sidecar exists
function ObjSceneTextured({ url, mtlUrl, resourcePath, textureUrls }: {
  url: string;
  mtlUrl: string;
  resourcePath: string;
  textureUrls?: Record<string, string>;
}) {
  const materials = useLoader(MTLLoader, mtlUrl, (loader) => {
    loader.setResourcePath(resourcePath);
    if (textureUrls && Object.keys(textureUrls).length > 0) {
      // Intercept texture URL construction so presigned URLs are used instead of
      // the unauthenticated resourcePath + filename that MTLLoader would build.
      loader.manager.setURLModifier((reqUrl) => {
        const filename = reqUrl.split('/').pop()?.split('?')[0] ?? '';
        return textureUrls[filename] ?? reqUrl;
      });
    }
  });
  useMemo(() => materials.preload(), [materials]);
  const object = useLoader(OBJLoader, url, (loader) => {
    loader.setMaterials(materials);
  });
  return (
    <Bounds fit clip observe margin={1.25}>
      <primitive object={object} />
    </Bounds>
  );
}

// OBJ without MTL — uses vertex colors when present, gray fallback otherwise
function ObjSceneFlat({ url }: { url: string }) {
  const object = useLoader(OBJLoader, url);
  const normalizedObject = useMemo(() => {
    const clone = object.clone();
    clone.traverse((child) => {
      if (child instanceof Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        const hasVertexColors = !!(child.geometry?.attributes?.color);
        child.material = new MeshStandardMaterial({
          vertexColors: hasVertexColors,
          ...(hasVertexColors ? {} : { color: '#d7dce2' }),
          roughness: 0.6,
          metalness: 0.0,
        });
        if (hasVertexColors && child.geometry.attributes.color) {
          (child.geometry.attributes.color as { colorSpace?: string }).colorSpace = SRGBColorSpace;
        }
      }
    });
    return clone;
  }, [object]);
  return (
    <Bounds fit clip observe margin={1.25}>
      <primitive object={normalizedObject} />
    </Bounds>
  );
}

function ObjViewer({ url, mtlUrl: providedMtlUrl, textureUrls }: {
  url: string;
  mtlUrl?: string;
  textureUrls?: Record<string, string>;
}) {
  // Strip query params for path derivation (presigned URLs have ?X-Amz-... params)
  const baseUrl = url.split('?')[0];
  const derivedMtlUrl = baseUrl.replace(/\.obj$/i, '.mtl');
  const mtlUrlToUse = providedMtlUrl ?? derivedMtlUrl;
  const resourcePath = baseUrl.substring(0, baseUrl.lastIndexOf('/') + 1);

  // Skip HEAD check when backend already confirmed the MTL exists (presigned URL provided)
  const [mtlState, setMtlState] = useState<'checking' | 'found' | 'missing'>(
    providedMtlUrl ? 'found' : 'checking',
  );
  useEffect(() => {
    if (providedMtlUrl) return;
    fetch(derivedMtlUrl, { method: 'HEAD' })
      .then((r) => setMtlState(r.ok ? 'found' : 'missing'))
      .catch(() => setMtlState('missing'));
  }, [derivedMtlUrl, providedMtlUrl]);

  return (
    <Canvas camera={{ position: [3, 2.2, 3], fov: 45 }} shadows>
      <color attach="background" args={['#0b0f16']} />
      <ambientLight intensity={0.65} />
      <directionalLight position={[5, 8, 4]} intensity={1.2} castShadow />
      <Suspense fallback={null}>
        <Stage environment="city" intensity={0.55} adjustCamera={false}>
          {mtlState === 'found'
            ? <ObjSceneTextured url={url} mtlUrl={mtlUrlToUse} resourcePath={resourcePath} textureUrls={textureUrls} />
            : mtlState === 'missing'
            ? <ObjSceneFlat url={url} />
            : null}
        </Stage>
      </Suspense>
      <OrbitControls makeDefault enableDamping />
    </Canvas>
  );
}

export function AgentArtifactViewer({
  artifact,
  onClose,
}: {
  artifact: AgentArtifact;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const title = artifact.label || t.viewerAgent.outputFile;

  return (
    <div className="fixed inset-0 z-[1400] bg-black/72 p-4 text-white backdrop-blur-sm">
      <div className="mx-auto flex h-full max-w-6xl flex-col overflow-hidden rounded-lg border border-white/12 bg-[#0d1117] shadow-2xl">
        <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#0CB4E4]/15 text-[#0CB4E4]">
            {artifactIcon(artifact.kind)}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-sm font-semibold">{title}</h2>
            <p className="truncate text-xs text-white/45">{artifact.path}</p>
          </div>
          {artifact.url && (
            <a
              href={artifact.url}
              target="_blank"
              rel="noreferrer"
              className="flex h-8 items-center gap-1.5 rounded-md border border-white/10 px-2.5 text-xs text-white/75 transition-colors hover:border-white/20 hover:text-white"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t.artifact.openTab}
            </a>
          )}
          {artifact.url && (
            <a
              href={artifact.url}
              download={artifact.label}
              className="flex h-8 items-center gap-1.5 rounded-md border border-white/10 px-2.5 text-xs text-white/75 transition-colors hover:border-white/20 hover:text-white"
            >
              <Download className="h-3.5 w-3.5" />
              {t.viewerAgent.download}
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-white/65 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close artifact viewer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative min-h-0 flex-1 bg-black">
          {!artifact.url ? (
            <div className="flex h-full items-center justify-center p-6 text-center">
              <div className="max-w-lg rounded-md border border-white/10 bg-white/[0.04] p-5">
                <p className="text-sm font-medium">{t.artifact.notBrowserReadable}</p>
                <p className="mt-2 text-xs leading-relaxed text-white/55">
                  {t.artifact.filesystemPathNote}
                </p>
                <code className="mt-4 block break-all rounded bg-black/35 p-2 text-left text-[11px] text-white/55">
                  {artifact.path}
                </code>
              </div>
            </div>
          ) : artifact.kind === 'video' ? (
            <video src={artifact.url} controls className="h-full w-full bg-black object-contain" />
          ) : artifact.kind === 'pdf' ? (
            <iframe src={artifact.url} title={title} className="h-full w-full border-0 bg-white" />
          ) : artifact.kind === 'obj' ? (
            <ObjViewer url={artifact.url} mtlUrl={artifact.mtlUrl} textureUrls={artifact.textureUrls} />
          ) : artifact.kind === 'image' ? (
            <div className="flex h-full items-center justify-center bg-black/60 p-4">
              <img src={artifact.url} alt={artifact.label} className="max-h-full max-w-full rounded object-contain shadow-lg" />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-center">
              <p className="text-sm text-white/60">Preview is not available for this file type.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
